import { Controller } from "@hotwired/stimulus";

// GR-3302: repeating line rows on the quote form. The form used to write
// exactly one line item, hardcoded to cost_category "other", and edit rewrote
// only the first of them, so a seeded five-line quote opened showing one line.
//
// Rows are server-rendered and the blank one lives in a <template>, so with JS
// disabled every existing line is still editable; only adding and removing
// need this controller.
export default class QuoteLineItemsController extends Controller {
  static targets = ["rows", "row", "template", "total", "rowTotal", "quantity", "unitPrice"];

  connect() {
    this.recalculate();
  }

  addRow() {
    this.appendRow();
  }

  appendRow() {
    const html = this.templateTarget.innerHTML.replace(
      /__INDEX__/g,
      String(this.nextIndex())
    );
    this.rowsTarget.insertAdjacentHTML("beforeend", html);
    this.recalculate();
    return this.rowsTarget.lastElementChild;
  }

  // GR-3355: a row asked for from outside the form — the Request Context
  // sidebar's AV list, which can see what the request wanted but not the form
  // that quotes it. It lands through the same template Add Line clones, so the
  // row markup stays in the view and there is one way to write a line.
  //
  // A form whose request asked for no AV opens on a single blank spare row, and
  // adding beside it would leave a hole above the line just asked for, so the
  // spare is filled first.
  addRequestedRow(event) {
    const { description, quantity, unitPrice, category } = event.detail ?? {};
    const row = this.blankRow() ?? this.appendRow();

    this.writeField(row, "description", description);
    this.writeField(row, "quantity", quantity);
    this.writeField(row, "unit_price", unitPrice);
    this.selectCategory(row, category);
    this.recalculate();
    this.lineField(row, "description")?.focus();
  }

  // A row nobody has put anything in. Quantity is not read: the spare row
  // arrives prefilled to 1, which is the same rule Invoice.blank_line_row?
  // applies on the server before it drops an untouched row.
  blankRow() {
    return this.rowTargets.find(
      (row) =>
        !row.hidden &&
        !this.lineField(row, "description")?.value &&
        !this.lineField(row, "unit_price")?.value
    );
  }

  writeField(row, name, value) {
    const field = this.lineField(row, name);
    if (!field || value === undefined || value === null || value === "") return;

    field.value = value;
    if (name === "description") field.title = value;
  }

  // GR-3851: a description runs past its input, so the input carries its full
  // text as a title, kept in step as it is typed.
  syncTitle(event) {
    event.target.title = event.target.value;
  }

  // A category this quote type does not offer is left as the row found it,
  // rather than selecting nothing and posting a blank the model would reject.
  selectCategory(row, category) {
    const select = row.querySelector("select[name$='[cost_category]']");
    if (!select || !category) return;
    if (!Array.from(select.options).some((option) => option.value === category)) return;

    select.value = category;
  }

  lineField(row, name) {
    return row.querySelector(`input[name$='[${name}]']`);
  }

  // A persisted line is marked for destruction and hidden, so the server is
  // told about the deletion. An unsaved row just leaves the DOM.
  removeRow(event) {
    const row = event.target.closest("[data-quote-line-items-target='row']");
    if (!row) return;

    if (row.querySelector("input[name$='[id]']")) {
      row.querySelector("input[name$='[_destroy]']").value = "1";
      row.hidden = true;
    } else {
      row.remove();
    }
    this.recalculate();
  }

  // GR-3381: one walk of the rows now answers two questions, the quote's total
  // and each row's own, so a line can never disagree with the sum it is part
  // of.
  recalculate() {
    let cents = 0;

    this.rowTargets.forEach((row) => {
      const rowCents = row.hidden ? null : this.rowCents(row);
      this.writeRowTotal(row, rowCents);
      if (rowCents !== null) cents += rowCents;
    });

    if (this.hasTotalTarget) this.totalTarget.textContent = this.formatUsd(cents);
  }

  // An unfinished row shows nothing rather than $0.00. GR-3354's hotel prefill
  // opens the rooming, meeting space and catering lines with a quantity and no
  // rate, because the rate comes from the hotel, and $0.00 there would read as
  // a quote of nothing rather than a quote not yet given.
  writeRowTotal(row, cents) {
    const cell = row.querySelector("[data-quote-line-items-target='rowTotal']");
    if (!cell) return;

    cell.textContent = cents === null ? "" : this.formatUsd(cents);
    cell.classList.toggle("text-semibold", cents !== null);
    cell.classList.toggle("color-fg-muted", cents === null);
  }

  // null for a row somebody has not finished, which is a different answer from
  // nought: a line priced at zero is a price a person typed.
  rowCents(row) {
    const quantity = parseFloat(row.querySelector("input[name$='[quantity]']")?.value);
    const unitPrice = parseFloat(row.querySelector("input[name$='[unit_price]']")?.value);
    if (!Number.isFinite(quantity) || !Number.isFinite(unitPrice)) return null;

    return Math.round(quantity * Math.round(unitPrice * 100));
  }

  // Index only has to be unique within the submitted params, so keep counting
  // up past rows that have been removed.
  //
  // GR-3913: counted from the highest index already in a row's names, not from
  // how many rows there are. An unsaved row that is removed leaves the DOM, so
  // a count reused the last row's index and the server kept only one of them.
  nextIndex() {
    this.rowCounter = Math.max(this.rowCounter ?? 0, this.highestIndex() + 1);
    return this.rowCounter++;
  }

  highestIndex() {
    return this.rowTargets.reduce((highest, row) => {
      const match = row
        .querySelector("[name*='[line_items_attributes]']")
        ?.name.match(/\[line_items_attributes\]\[(\d+)\]/);
      return match ? Math.max(highest, Number(match[1])) : highest;
    }, -1);
  }

  // GR-3476: the same figure the server wrote, cents and all. money-rails
  // renders a whole amount without them and everything else with both of them
  // ($7,560, and $37.50 rather than $37.5), so connect() rewriting every cell
  // on the page must land on the same string or the quote visibly changes the
  // moment the controller starts.
  formatUsd(cents) {
    const digits = cents % 100 === 0 ? 0 : 2;

    return (cents / 100).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  }
}
